import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { CheckCircle, Envelope, BellRinging, User, ArrowUpRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user, loading, refresh } = useAuth();
  const [prefs, setPrefs] = useState({ email_subscribed: true, digest_frequency: "weekly", cfp_reminders: true });
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);

  useEffect(() => {
    if (user && user.role) {
      setPrefs(user.preferences || { email_subscribed: true, digest_frequency: "weekly", cfp_reminders: true });
      setName(user.name || "");
      api.get("/bookmarks").then((r) => setBookmarks(r.data || [])).catch(() => {});
    }
  }, [user]);

  if (loading) return <div className="px-6 md:px-10 lg:px-16 py-16">{t("common.loading")}</div>;
  if (!user || !user.role) {
    return (
      <div data-testid="settings-page" className="px-6 md:px-10 lg:px-16 py-16 max-w-md mx-auto text-center">
        <h1 className="font-display font-bold text-4xl mb-3">{t("common.pleaseSignIn")}</h1>
        <Link to="/login" className="btn-primary">{t("common.signIn")}</Link>
      </div>
    );
  }

  async function save(partial) {
    setBusy(true);
    try {
      const payload = { ...partial };
      const { data } = await api.put("/me/preferences", payload);
      setPrefs({
        email_subscribed: data.email_subscribed,
        digest_frequency: data.digest_frequency,
        cfp_reminders: data.cfp_reminders,
      });
      if (data.name !== undefined) setName(data.name);
      await refresh();
      toast.success(t("settings.savedToast"));
    } catch (e) {
      toast.error(t("settings.saveFailedToast"));
    } finally { setBusy(false); }
  }

  const freqLabels = { weekly: t("settings.weekly"), monthly: t("settings.monthly"), never: t("settings.never") };
  const digestVal = prefs.digest_frequency === "never" ? "—" : freqLabels[prefs.digest_frequency];

  return (
    <div data-testid="settings-page" className="px-6 md:px-10 lg:px-16 py-12 md:py-16 max-w-4xl">
      <div className="overline mb-3">{t("settings.breadcrumb")}</div>
      <h1 className="font-display font-black text-5xl md:text-6xl tracking-[-0.04em] leading-none mb-10">{t("settings.title")}</h1>

      {/* Account */}
      <section className="border-t border-zinc-950 pt-8 pb-12">
        <div className="flex items-center gap-2 mb-2"><User size={18} weight="duotone" /><h2 className="font-display font-bold text-2xl tracking-tight">{t("settings.accountTitle")}</h2></div>
        <p className="text-sm text-zinc-500 mb-6">{t("settings.accountHelp")}</p>
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label className="overline block mb-2">{t("settings.email")}</label>
            <div className="input-line opacity-60" data-testid="settings-email">{user.email}</div>
          </div>
          <div>
            <label className="overline block mb-2">{t("settings.displayName")}</label>
            <input data-testid="settings-name" value={name} onChange={(e) => setName(e.target.value)} className="input-line" placeholder={t("settings.yourName")} />
          </div>
        </div>
        <div className="mt-5">
          <button data-testid="settings-save-name" onClick={() => save({ name })} disabled={busy || !name.trim()} className="btn-primary">{t("settings.saveProfile")}</button>
        </div>
      </section>

      {/* Email */}
      <section className="border-t border-zinc-950 pt-8 pb-12">
        <div className="flex items-center gap-2 mb-2"><Envelope size={18} weight="duotone" /><h2 className="font-display font-bold text-2xl tracking-tight">{t("settings.emailTitle")}</h2></div>
        <p className="text-sm text-zinc-500 mb-6">{t("settings.emailHelp")}</p>

        <ToggleRow
          label={t("settings.digestLabel")}
          help={t("settings.digestHelp")}
          activeLabel={t("settings.active")}
          checked={prefs.email_subscribed && prefs.digest_frequency !== "never"}
          onChange={(v) => save({ email_subscribed: v, digest_frequency: v ? (prefs.digest_frequency === "never" ? "weekly" : prefs.digest_frequency) : "never" })}
          testid="settings-digest-toggle"
          disabled={busy}
        />

        {prefs.email_subscribed && prefs.digest_frequency !== "never" && (
          <div className="ms-10 mt-3">
            <label className="overline block mb-2">{t("settings.frequency")}</label>
            <div className="flex gap-2">
              {["weekly", "monthly"].map((f) => (
                <button
                  key={f}
                  onClick={() => save({ digest_frequency: f })}
                  data-testid={`settings-freq-${f}`}
                  className={(prefs.digest_frequency === f ? "btn-primary" : "btn-secondary") + " text-sm"}
                  style={{ padding: ".5rem 1rem" }}
                >
                  {freqLabels[f]}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6">
          <ToggleRow
            label={t("settings.cfpLabel")}
            help={t("settings.cfpHelp")}
            activeLabel={t("settings.active")}
            checked={prefs.cfp_reminders}
            onChange={(v) => save({ cfp_reminders: v })}
            testid="settings-cfp-toggle"
            disabled={busy}
          />
        </div>
      </section>

      {/* Activity snapshot */}
      <section className="border-t border-zinc-950 pt-8 pb-12">
        <div className="flex items-center gap-2 mb-6"><BellRinging size={18} weight="duotone" /><h2 className="font-display font-bold text-2xl tracking-tight">{t("settings.activityTitle")}</h2></div>
        <div className="grid sm:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
          <Stat n={bookmarks.length} l={t("settings.bookmarks")} />
          <Stat n={(user.attending || []).length} l={t("settings.attending")} />
          <Stat n={digestVal} l={t("settings.digest")} />
        </div>
        <div className="mt-6 flex gap-3">
          <Link to="/bookmarks" data-testid="settings-go-bookmarks" className="btn-secondary">{t("settings.viewBookmarks")} <ArrowUpRight size={14} data-arrow="forward" /></Link>
        </div>
      </section>

      <section className="border-t border-zinc-200 pt-6 text-xs text-zinc-500 font-mono">
        {t("settings.deleteHelp")} <a href="mailto:hello@conferencely.org" className="underline">hello@conferencely.org</a>.
      </section>
    </div>
  );
}

function ToggleRow({ label, help, activeLabel, checked, onChange, testid, disabled }) {
  return (
    <div className="flex items-start gap-4 py-2">
      <button
        role="switch"
        aria-checked={checked}
        onClick={() => !disabled && onChange(!checked)}
        data-testid={testid}
        disabled={disabled}
        className={(checked ? "bg-zinc-950" : "bg-zinc-300") + " relative w-11 h-6 rounded-full transition-colors flex-shrink-0 mt-0.5 disabled:opacity-50"}
      >
        <span className={(checked ? "translate-x-5" : "translate-x-0.5") + " absolute top-0.5 left-0 w-5 h-5 bg-white rounded-full transition-transform"} />
      </button>
      <div className="flex-1">
        <div className="font-medium text-base">{label}</div>
        <div className="text-sm text-zinc-500 mt-0.5">{help}</div>
        {checked && <div className="mt-1 text-xs text-blue-700 font-mono flex items-center gap-1"><CheckCircle size={12} weight="fill" /> {activeLabel}</div>}
      </div>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div className="bg-white p-5">
      <div className="font-display font-black text-3xl tracking-tight tabular leading-none">{n}</div>
      <div className="overline mt-2">{l}</div>
    </div>
  );
}
